import React, { forwardRef, useState, Children, isValidElement, useRef, useMemo, cloneElement } from 'react';
import { uniqueId, getDisplayName, safeRest, KEYS } from '@wds/utils';
import { Box } from '@wds/box';
import { List } from '@wds/list';
import { Pane } from '@wds/internal-components';
import { IconChevronUp, IconChevronDown } from '@wds/icons';
import { Typography } from '@wds/typography';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }
    return t;
  };
  return _assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var AccordionItem = forwardRef(function (_a, ref) {
  var addOnLeft = _a.addOnLeft,
    addOnRight = _a.addOnRight,
    affordance = _a.affordance,
    ariaControls = _a.ariaControls,
    _onKeyDown = _a.onKeyDown,
    children = _a.children,
    color = _a.color,
    disabled = _a.disabled,
    expanded = _a.expanded,
    id = _a.id,
    interactive = _a.interactive,
    transparent = _a.transparent,
    marked = _a.marked,
    _onClick = _a.onClick,
    paneComponent = _a.paneComponent,
    upgrade = _a.upgrade;
  var tabIndex = disabled ? '-1' : 0;
  return React.createElement(List.Item, {
    accordion: true,
    addOnLeft: addOnLeft,
    addOnRight: addOnRight,
    affordance: affordance,
    "aria-controls": ariaControls,
    onKeyDown: function onKeyDown(e) {
      return _onKeyDown === null || _onKeyDown === void 0 ? void 0 : _onKeyDown(e, !expanded);
    },
    color: color,
    disabled: disabled,
    expanded: expanded,
    id: id,
    interactive: interactive,
    marked: marked,
    onClick: function onClick(e) {
      return _onClick === null || _onClick === void 0 ? void 0 : _onClick(e, !expanded);
    },
    paneComponent: paneComponent,
    role: "button",
    upgrade: upgrade,
    tabIndex: tabIndex,
    ref: ref,
    transparent: transparent
  }, children);
});
AccordionItem.defaultProps = {
  addOnLeft: undefined,
  addOnRight: undefined,
  affordance: undefined,
  ariaControls: undefined,
  onKeyDown: undefined,
  children: undefined,
  color: undefined,
  disabled: false,
  id: undefined,
  interactive: false,
  marked: false,
  onClick: undefined,
  paneComponent: undefined,
  upgrade: false,
  subtitle: undefined,
  titleSize: 'md',
  transparent: false
};
AccordionItem.displayName = 'AccordionItem';
var Accordion = function Accordion(_a) {
  var _b, _c;
  var multiple = _a.multiple,
    interactive = _a.interactive,
    noRules = _a.noRules,
    dense = _a.dense,
    transparent = _a.transparent,
    onChange = _a.onChange,
    id = _a.id,
    children = _a.children,
    _d = _a.padding,
    padding = _d === void 0 ? true : _d,
    rest = __rest(_a, ["multiple", "interactive", "noRules", "dense", "transparent", "onChange", "id", "children", "padding"]);
  // If any of the children have an "expanded={true|false}" prop
  // then we assume the outside component is maintaining the expanded
  // state of the AccordionItems and we ignore props like 'multiple'.
  // It defers to the props to control when panels are expanded/collapsed
  var areAnyItemsControlled = false;
  var defaultExpandedCount = 0;
  var _e = useState((_c = (_b = Children.map(children, function (child, index) {
      if (!child) {
        return null;
      }
      if (child.props.expanded !== undefined) {
        areAnyItemsControlled = true;
      }
      // ensure we only have 1 defaultExpanded item unless multiple=true
      if (child.props.defaultExpanded) {
        defaultExpandedCount += 1;
      }
      return isValidElement(child) && (child.props.expanded || child.props.defaultExpanded) && (multiple || defaultExpandedCount <= 1) ? index : null;
    })) === null || _b === void 0 ? void 0 : _b.filter(function (val) {
      return val !== null;
    })) !== null && _c !== void 0 ? _c : []),
    expandedIndices = _e[0],
    setExpandedIndices = _e[1];
  // Once a child is identified as controlled, the Accordion is assumed to be
  // controlled for the lifetime of the component, as per React conventions
  var isExpandedStateControlled = useState(areAnyItemsControlled)[0];
  var accListRef = useRef(null);
  var uniqueAccName = useMemo(function () {
    return uniqueId('wrench-acc-');
  }, []);
  var uniqueAccPaneName = useMemo(function () {
    return uniqueId('wrench-accPane-');
  }, []);
  if (areAnyItemsControlled !== isExpandedStateControlled) {
    if (areAnyItemsControlled) {
      // eslint-disable-next-line no-console
      console.error('Accordion is changing from being uncontrolled to being controlled. If you use the optional `expanded` prop to Accordion.Item, make sure its value is always {true|false}, and not {undefined|null}.');
    } else {
      // eslint-disable-next-line no-console
      console.error('Accordion is changing from being controlled to being uncontrolled. If you use the optional `expanded` prop to Accordion.Item, make sure its value is always {true|false}, and not {undefined|null}.');
    }
  }
  /**
   * @param {number} ind
   * @return {void}
   */
  var handleOnChange = function handleOnChange(evt, index, itemId, isExpanding) {
    // Since dom node is a div, we can't check the disabled property
    if (evt.currentTarget !== null && evt.currentTarget.tabIndex > -1) {
      var newExpandedIndices = [];
      if (isExpandedStateControlled) {
        newExpandedIndices = Children.map(children, function (child, i) {
          // If the current index is changing
          if (i === index) {
            // include the index if it is expanding
            return isExpanding ? i : null;
          }
          // For all others, include index if it is already expanded
          return child.props.expanded ? i : null;
        }).filter(function (val) {
          return Number.isInteger(val);
        });
      } else if (!multiple) {
        newExpandedIndices = expandedIndices[0] === index ? [] : [index];
      } else if (expandedIndices.includes(index)) {
        newExpandedIndices = expandedIndices.filter(function (item) {
          return item !== index;
        });
      } else {
        newExpandedIndices = expandedIndices.concat(index);
      }
      setExpandedIndices(newExpandedIndices);
      if (onChange) {
        onChange(newExpandedIndices, index, itemId, isExpanding);
      }
    }
  };
  /**
  * Accordion according to Accordion Design Pattern in WAI-ARIA Authoring Practices 1.1
  * see https://www.w3.org/TR/wai-aria-practices/examples/accordion/accordion.html
  *
  * Keyboard Support:
  *
  ```
  Space or Enter
  When focus is on the accordion header of a collapsed section, expands the section.
  Tab
  Moves focus to the next focusable element.
  All focusable elements in the accordion are included in the page Tab sequence.
  Shift + Tab
  Moves focus to the previous focusable element.
  All focusable elements in the accordion are included in the page Tab sequence.
  Down Arrow
  When focus is on an accordion header, moves focus to the next accordion header.
  When focus is on last accordion header, moves focus to first accordion header.
  Up Arrow
  When focus is on an accordion header, moves focus to the previous accordion header.
  When focus is on first accordion header, moves focus to last accordion header.
  Home
  When focus is on an accordion header, moves focus to the first accordion header.
  End
  When focus is on an accordion header, moves focus to the last accordion header.
  ```
  */
  var handleKeyDown = function handleKeyDown(evt, ind, itemId, isExpanding) {
    if (evt.key === KEYS.SPACE || evt.key === KEYS.ENTER) {
      evt.preventDefault();
      handleOnChange(evt, ind, itemId, isExpanding);
      return;
    }
    var accList = accListRef.current ? Array.from(accListRef.current.children).map(function (acc) {
      return acc.firstChild;
    }) : [];
    var accListLen = accList.length - 1;
    var newFocusTarget = null;
    switch (evt.key) {
      case KEYS.DOWN:
        evt.preventDefault();
        newFocusTarget = ind < accListLen ? accList[ind + 1] : accList[0];
        break;
      case KEYS.UP:
        evt.preventDefault();
        newFocusTarget = ind === 0 ? accList[accListLen] : accList[ind - 1];
        break;
      case KEYS.HOME:
        evt.preventDefault();
        newFocusTarget = accList[0];
        break;
      case KEYS.END:
        evt.preventDefault();
        newFocusTarget = accList[accListLen];
        break;
    }
    // Guard Null Case
    if (newFocusTarget !== null) {
      newFocusTarget.focus();
    }
  };
  /**
   * @param {node} paneChildren
   * @param {string} labelledBy
   * @param {string} paneId
   * @return {node}
   */
  var renderPane = function renderPane(paneChildren, labelledBy, paneId, expanded) {
    var panePadding = padding;
    if (getDisplayName(paneChildren) === 'List') {
      panePadding = false;
    }
    var paneProps = {
      padding: panePadding,
      labelledBy: labelledBy,
      hide: !expanded,
      role: 'region',
      id: paneId
    };
    return React.createElement(Pane, _assign({
      transparent: true
    }, paneProps), paneChildren);
  };
  /**
   *
   * @param {node} child
   * @param {number} index
   * @param {boolean} expanded
   * @return {node}
   */
  var renderAccordionElement = function renderAccordionElement(child, index, expanded) {
    var _a, _b;
    var accordionName = (_a = child.props.id) !== null && _a !== void 0 ? _a : "".concat(uniqueAccName, "-").concat(index);
    var accordionPaneName = (_b = child.props.ariaControls) !== null && _b !== void 0 ? _b : "".concat(uniqueAccPaneName, "-").concat(index);
    var paneComponent = renderPane(child.props.children, accordionName, accordionPaneName, expanded);
    var titleVariant = child.props.titleSize === 'lg' ? 'cardTitle' : 'body';
    var childElements = React.createElement(Box, {
      my: 4
    }, React.createElement(Box, {
      mb: 2
    }, React.createElement(Typography, {
      component: "strong",
      variant: titleVariant
    }, child.props.title)), child.props.subtitle && React.createElement(Typography, {
      variant: "bodySm",
      color: "secondary"
    }, child.props.subtitle));
    return cloneElement(child, {
      children: childElements,
      expanded: expanded,
      key: accordionName,
      id: accordionName,
      ariaControls: accordionPaneName,
      paneComponent: paneComponent,
      affordance: expanded ? React.createElement(IconChevronUp, {
        title: "Chevron Up"
      }) : React.createElement(IconChevronDown, {
        title: "Chevron Down"
      }),
      onClick: function onClick(evt, isExpanding) {
        return handleOnChange(evt, index, accordionName, isExpanding);
      },
      onKeyDown: function onKeyDown(evt, isExpanding) {
        return handleKeyDown(evt, index, accordionName, isExpanding);
      }
    });
  };
  var accordionList = Children.map(children, function (child, index) {
    var isExpandedItem = isExpandedStateControlled ? !!child.props.expanded : expandedIndices.includes(index);
    if (!isValidElement(child)) {
      return null;
    }
    return getDisplayName(child) === 'ListItem' ? child : renderAccordionElement(child, index, isExpandedItem);
  });
  return React.createElement(List, _assign({
    ref: accListRef,
    noRules: noRules,
    interactive: interactive,
    dense: dense,
    transparent: transparent,
    id: id
  }, safeRest(rest)), accordionList);
};
Accordion.Item = AccordionItem;
export { Accordion };